<template>
  <div 
    class="sd-kpi-card"
    :class="[
      variantClass,
      colorClass,
      { 
        'sd-kpi-card--compact': compact || variant === 'compact',
        'sd-kpi-card--horizontal': horizontal
      }
    ]"
  >
    <div class="flex items-center" :class="horizontal ? 'gap-3' : 'justify-between'">
      <div v-if="icon && horizontal" class="flex-shrink-0">
        <div 
          class="w-10 h-10 rounded-lg flex items-center justify-center"
          :class="iconBgClass"
        >
          <BaseIcon :icon="icon" :class="iconColorClass" class="w-5 h-5" />
        </div>
      </div>
      <div class="flex-1 min-w-0">
        <p class="sd-kpi-card__title">
          {{ title }}
        </p>
        <p class="sd-kpi-card__value" :class="valueColorClass">
          {{ formattedValue }}
        </p>
        <p v-if="subtitle" class="sd-kpi-card__subtitle">
          {{ subtitle }}
        </p>
      </div>
      <div v-if="icon && !horizontal" class="flex-shrink-0 ml-4">
        <div 
          class="w-12 h-12 rounded-xl flex items-center justify-center"
          :class="iconBgClass"
        >
          <BaseIcon :icon="icon" :class="iconColorClass" class="w-5 h-5" />
        </div>
      </div>
    </div>
    
    <!-- Trend indicator -->
    <div v-if="showTrend && trend !== null" class="mt-3">
      <span 
        class="sd-kpi-card__trend"
        :class="trend >= 0 ? 'sd-kpi-card__trend--positive' : 'sd-kpi-card__trend--negative'"
      >
        <BaseIcon 
          :icon="trend >= 0 ? 'arrow-up' : 'arrow-down'" 
          class="w-3 h-3"
        />
        {{ Math.abs(trend) }}%
        <span class="font-normal opacity-75 ml-1">{{ $t('sales_dashboard.vs_previous_period') }}</span>
      </span>
    </div>
    
    <!-- Progress bar -->
    <div v-if="showProgress && maxValue" class="mt-3">
      <div class="flex justify-between text-xs mb-1.5">
        <span class="text-secondary-500">{{ $t('sales_dashboard.progress') }}</span>
        <span class="font-semibold" :class="valueColorClass">{{ progressPercentage }}%</span>
      </div>
      <div class="sd-progress">
        <div 
          class="sd-progress__fill"
          :class="progressBarClass"
          :style="{ width: `${Math.min(progressPercentage, 100)}%` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KPICard',
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: [Number, String],
      required: true
    },
    subtitle: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    format: {
      type: String,
      default: 'number', // 'number', 'currency', 'percentage', 'decimal'
      validator: val => ['number', 'currency', 'percentage', 'decimal'].includes(val)
    },
    color: {
      type: String,
      default: 'primary', // 'primary', 'accent', 'warning', 'danger', 'info', 'success'
      validator: val => ['primary', 'accent', 'warning', 'danger', 'info', 'success'].includes(val)
    },
    variant: {
      type: String,
      default: 'default', // 'default', 'gradient-blue', 'gradient-orange', 'compact'
      validator: val => ['default', 'gradient-blue', 'gradient-orange', 'compact'].includes(val)
    },
    trend: {
      type: Number,
      default: null
    },
    showTrend: {
      type: Boolean,
      default: false
    },
    maxValue: {
      type: Number,
      default: null
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    suffix: {
      type: String,
      default: ''
    }
  },
  computed: {
    variantClass() {
      const variants = {
        'gradient-blue': 'sd-kpi-card--gradient-blue',
        'gradient-orange': 'sd-kpi-card--gradient-orange',
        'compact': '',
        'default': ''
      };
      return variants[this.variant] || '';
    },
    colorClass() {
      if (this.variant === 'gradient-blue' || this.variant === 'gradient-orange') {
        return '';
      }
      const colors = {
        primary: 'sd-kpi-card--primary',
        accent: 'sd-kpi-card--accent',
        success: 'sd-kpi-card--success',
        warning: 'sd-kpi-card--accent',
        danger: 'sd-kpi-card--accent',
        info: 'sd-kpi-card--primary'
      };
      return colors[this.color] || colors.primary;
    },
    formattedValue() {
      if (typeof this.value === 'string') return this.value + this.suffix;
      
      const num = Number(this.value) || 0;
      
      if (this.format === 'currency') {
        return this.formatCurrency(num) + this.suffix;
      } else if (this.format === 'percentage') {
        return `${num.toFixed(1)}%` + this.suffix;
      } else if (this.format === 'decimal') {
        return num.toFixed(1) + this.suffix;
      } else {
        return this.formatNumber(num) + this.suffix;
      }
    },
    valueColorClass() {
      if (this.variant === 'gradient-blue' || this.variant === 'gradient-orange') {
        return 'text-white';
      }
      const colors = {
        primary: 'text-securdia-blue',
        accent: 'text-securdia-orange',
        warning: 'text-yellow-600',
        danger: 'text-red-600',
        info: 'text-blue-500',
        success: 'text-green-600'
      };
      return colors[this.color] || colors.primary;
    },
    iconBgClass() {
      if (this.variant === 'gradient-blue' || this.variant === 'gradient-orange') {
        return 'bg-white/20';
      }
      const colors = {
        primary: 'bg-primary-50',
        accent: 'bg-accent-50',
        warning: 'bg-yellow-50',
        danger: 'bg-red-50',
        info: 'bg-blue-50',
        success: 'bg-green-50'
      };
      return colors[this.color] || colors.primary;
    },
    iconColorClass() {
      if (this.variant === 'gradient-blue' || this.variant === 'gradient-orange') {
        return 'text-white';
      }
      const colors = {
        primary: 'text-securdia-blue',
        accent: 'text-securdia-orange',
        warning: 'text-yellow-500',
        danger: 'text-red-500',
        info: 'text-blue-400',
        success: 'text-green-500'
      };
      return colors[this.color] || colors.primary;
    },
    progressBarClass() {
      const colors = {
        primary: 'sd-progress__fill--primary',
        accent: 'sd-progress__fill--accent',
        success: 'sd-progress__fill--success',
        warning: 'sd-progress__fill--accent',
        danger: 'sd-progress__fill--accent',
        info: 'sd-progress__fill--primary'
      };
      return colors[this.color] || colors.primary;
    },
    progressPercentage() {
      if (!this.maxValue || this.maxValue === 0) return 0;
      return Math.round((Number(this.value) / this.maxValue) * 100);
    }
  },
  methods: {
    formatCurrency(value) {
      if (this.compact) {
        if (Math.abs(value) >= 1000000) {
          return `${(value / 1000000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}M kr`;
        }
        if (Math.abs(value) >= 1000) {
          return `${(value / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 0 })}k kr`;
        }
      }
      return `${value.toLocaleString('sv-SE', { maximumFractionDigits: 0 })} kr`;
    },
    formatNumber(value) {
      if (this.compact || this.variant === 'compact') {
        if (Math.abs(value) >= 1000000) {
          return `${(value / 1000000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })}M`;
        }
        if (Math.abs(value) >= 1000) {
          return `${(value / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 0 })}k`;
        }
      }
      return value.toLocaleString('sv-SE', { maximumFractionDigits: 0 });
    }
  }
};
</script>

<style scoped>
/* Component uses global .sd-* classes from sales_dashboard.scss */
</style>
